import React, { useState, useEffect } from 'react';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
/**
 * ShareCategoryModal - Share categories with specific users or groups
 * 
 * Features:
 * - Share with specific users (friends)
 * - Share with groups
 * - Generate shareable links
 * - Add optional message
 */
const ShareCategoryModal = ({ 
  category, 
  isOpen, 
  onClose, 
  token,
  showToast 
}) => {
  const { get, post, del: xhrDel, put } = useXHR(token);
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'groups' | 'link'
  const [friends, setFriends] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Fetch friends and groups when modal opens
  useEffect(() => {
    if (isOpen && token) {
      fetchFriends();
      fetchGroups();
    }
  }, [isOpen, token]);
  
  const fetchFriends = async () => {
    try {
      get('/friends', {
      onSuccess: (data) => setFriends(data.friends || [])
    })
    } catch (err) {
      console.error('Failed to fetch friends:', err);
    }
  };
  
  const fetchGroups = async () => {
    try {
      const data = await get('/groups');
      setGroups(data.groups || data || []);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  };
  
  const handleShareWithUsers = async () => {
    if (selectedUsers.length === 0) {
      showToast && showToast('Please select at least one user', 'error');
      return;
    }
    
    setLoading(true);
    try {
      const categoryId = category.id || category._id;
      const data = await post(`/categories/${categoryId}/share-with-users`, {
        body: { user_ids: selectedUsers, message }
      });
      showToast && showToast(`Shared with ${data.shared_count} user(s)!`, 'success');
      setSelectedUsers([]);
      setMessage('');
      onClose();
    } catch (err) {
      showToast && showToast('Share failed: ' + (typeof err === 'string' ? err : err.message), 'error');
    }
    setLoading(false);
  };
  
  const handleShareWithGroups = async () => {
    if (selectedGroups.length === 0) {
      showToast && showToast('Please select at least one group', 'error');
      return;
    }
    
    setLoading(true);
    try {
      const categoryId = category.id || category._id;
      const data = await post(`/categories/${categoryId}/share-with-groups`, {
        body: { group_ids: selectedGroups, message }
      });
      showToast && showToast(`Shared with ${data.shared_count} group(s)!`, 'success');
      setSelectedGroups([]);
      setMessage('');
      onClose();
    } catch (err) {
      showToast && showToast('Share failed: ' + (typeof err === 'string' ? err : err.message), 'error');
    }
    setLoading(false);
  };
  
  const handleGenerateLink = async () => {
    setLoading(true);
    try {
      const categoryId = category.id || category._id;
      const data = await post(`/categories/${categoryId}/share-link`);
      setShareLink(data.share_url || data.url);
      showToast && showToast('Share link generated!', 'success');
    } catch (err) {
      showToast && showToast(typeof err === 'string' ? err : 'Failed to generate link', 'error');
    }
    setLoading(false);
  };
  
  const copyToClipboard = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      showToast && showToast('Link copied to clipboard!', 'success');
    }
  };
  
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };
  
  const toggleGroupSelection = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };
  
  const filteredFriends = friends.filter(f => 
    !searchQuery || 
    (f.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (f.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const filteredGroups = groups.filter(g => 
    !searchQuery || 
    (g.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  if (!isOpen) return null;
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 20
      }}
      onClick={onClose}
      data-testid="share-modal-overlay"
    >
      <div 
        style={{
          background: 'linear-gradient(135deg, #1e1e2f 0%, #2d1f3d 100%)',
          borderRadius: 16,
          width: '100%',
          maxWidth: 500,
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={e => e.stopPropagation()}
        data-testid="share-modal-container"
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#0F172A', fontSize: '1.2rem' }}>
              Share Category
            </h2>
            <p style={{ margin: '4px 0 0', color: '#a1a1aa', fontSize: '0.85rem' }}>
              {category?.name || 'Untitled Category'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '50%',
              width: 32,
              height: 32,
              color: '#0F172A',
              cursor: 'pointer',
              fontSize: '1.1rem'
            }}
            data-testid="close-share-modal"
          >
            ×
          </button>
        </div>
        
        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          padding: '0 24px'
        }}>
          {['users', 'groups', 'link'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #FDE047' : '2px solid transparent',
                color: activeTab === tab ? '#fff' : '#a1a1aa',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'all 0.2s'
              }}
              data-testid={`share-tab-${tab}`}
            >
              {tab === 'users' && '👥 Users'}
              {tab === 'groups' && '🏘️ Groups'}
              {tab === 'link' && '🔗 Link'}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {/* Search (for users and groups) */}
          {(activeTab === 'users' || activeTab === 'groups') && (
            <input
              type="text"
              placeholder={`Search ${activeTab === 'users' ? 'friends' : 'groups'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'rgba(255, 255, 255, 0.85)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                color: '#0F172A',
                fontSize: '0.9rem',
                marginBottom: 16
              }}
              data-testid="share-search-input"
            />
          )}
          
          {/* Users Tab */}
          {activeTab === 'users' && (
            <div>
              {filteredFriends.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: 20 }}>
                  {friends.length === 0 ? 'No friends yet. Add friends to share categories!' : 'No matches found'}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {filteredFriends.map(friend => (
                    <label
                      key={friend.id || friend._id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 12px',
                        background: selectedUsers.includes(friend.id || friend._id) 
                          ? 'rgba(253, 224, 71, 0.2)' 
                          : 'rgba(255, 255, 255, 0.85)',
                        borderRadius: 8,
                        cursor: 'pointer',
                        border: `1px solid ${selectedUsers.includes(friend.id || friend._id) ? 'rgba(253, 224, 71, 0.4)' : 'transparent'}`,
                        transition: 'all 0.2s'
                      }}
                      data-testid={`friend-item-${friend.id || friend._id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(friend.id || friend._id)}
                        onChange={() => toggleUserSelection(friend.id || friend._id)}
                        style={{ accentColor: '#FDE047' }}
                      />
                      <div style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #FCD34D, #FDE047)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0F172A',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}>
                        {(friend.username || friend.email || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ color: '#0F172A', fontWeight: 500, fontSize: '0.9rem' }}>
                          {friend.username || friend.email?.split('@')[0]}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                          {friend.email}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              {/* Message */}
              <div style={{ marginTop: 16 }}>
                <label style={{ display: 'block', color: '#a1a1aa', fontSize: '0.85rem', marginBottom: 6 }}>
                  Add a message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Hey, check out this category!"
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(255, 255, 255, 0.85)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    color: '#0F172A',
                    fontSize: '0.9rem',
                    resize: 'vertical'
                  }}
                  data-testid="share-message-input"
                />
              </div>
              
              {/* Share Button */}
              <button
                onClick={handleShareWithUsers}
                disabled={loading || selectedUsers.length === 0}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginTop: 16,
                  background: selectedUsers.length > 0 
                    ? 'linear-gradient(135deg, #FCD34D, #FDE047)' 
                    : 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: 8,
                  color: '#0F172A',
                  fontWeight: 600,
                  cursor: selectedUsers.length > 0 ? 'pointer' : 'not-allowed',
                  opacity: loading ? 0.7 : 1
                }}
                data-testid="share-with-users-btn"
              >
                {loading ? 'Sharing...' : `Share with ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          )}
          
          {/* Groups Tab */}
          {activeTab === 'groups' && (
            <div>
              {filteredGroups.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: 20 }}>
                  {groups.length === 0 ? 'No groups yet. Create groups to share categories!' : 'No matches found'}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {filteredGroups.map(group => (
                    <label
                      key={group.id || group._id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 12px',
                        background: selectedGroups.includes(group.id || group._id) 
                          ? 'rgba(16, 185, 129, 0.2)' 
                          : 'rgba(255, 255, 255, 0.85)',
                        borderRadius: 8,
                        cursor: 'pointer',
                        border: `1px solid ${selectedGroups.includes(group.id || group._id) ? 'rgba(16, 185, 129, 0.4)' : 'transparent'}`,
                        transition: 'all 0.2s'
                      }}
                      data-testid={`group-item-${group.id || group._id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(group.id || group._id)}
                        onChange={() => toggleGroupSelection(group.id || group._id)}
                        style={{ accentColor: '#10b981' }}
                      />
                      <div style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, #059669, #10b981)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0F172A',
                        fontSize: '1rem'
                      }}>
                        🏘️
                      </div>
                      <div>
                        <div style={{ color: '#0F172A', fontWeight: 500, fontSize: '0.9rem' }}>
                          {group.name}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                          {group.member_count || 0} members
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              {/* Message */}
              <div style={{ marginTop: 16 }}>
                <label style={{ display: 'block', color: '#a1a1aa', fontSize: '0.85rem', marginBottom: 6 }}>
                  Add a message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Hey everyone, check out this category!"
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(255, 255, 255, 0.85)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    color: '#0F172A',
                    fontSize: '0.9rem',
                    resize: 'vertical'
                  }}
                  data-testid="share-group-message-input"
                />
              </div>
              
              {/* Share Button */}
              <button
                onClick={handleShareWithGroups}
                disabled={loading || selectedGroups.length === 0}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginTop: 16,
                  background: selectedGroups.length > 0 
                    ? 'linear-gradient(135deg, #059669, #10b981)' 
                    : 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: 8,
                  color: '#0F172A',
                  fontWeight: 600,
                  cursor: selectedGroups.length > 0 ? 'pointer' : 'not-allowed',
                  opacity: loading ? 0.7 : 1
                }}
                data-testid="share-with-groups-btn"
              >
                {loading ? 'Sharing...' : `Share with ${selectedGroups.length} Group${selectedGroups.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          )}
          
          {/* Link Tab */}
          {activeTab === 'link' && (
            <div>
              <p style={{ color: '#a1a1aa', fontSize: '0.9rem', marginBottom: 16 }}>
                Generate a shareable link that anyone can use to view and copy this category.
              </p>
              
              {shareLink ? (
                <div>
                  <div style={{
                    display: 'flex',
                    gap: 8,
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.85)',
                    borderRadius: 8,
                    marginBottom: 12
                  }}>
                    <input
                      type="text"
                      value={shareLink}
                      readOnly
                      style={{
                        flex: 1,
                        background: 'transparent',
                        border: 'none',
                        color: '#0F172A',
                        fontSize: '0.85rem'
                      }}
                      data-testid="share-link-input"
                    />
                    <button
                      onClick={copyToClipboard}
                      style={{
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #3b82f6, #FCD34D)',
                        border: 'none',
                        borderRadius: 6,
                        color: '#0F172A',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                      data-testid="copy-link-btn"
                    >
                      Copy
                    </button>
                  </div>
                  
                  <button
                    onClick={handleGenerateLink}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      color: '#a1a1aa',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                    data-testid="regenerate-link-btn"
                  >
                    Generate New Link
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleGenerateLink}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'linear-gradient(135deg, #3b82f6, #FCD34D)',
                    border: 'none',
                    borderRadius: 8,
                    color: '#0F172A',
                    fontWeight: 600,
                    cursor: 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                  data-testid="generate-link-btn"
                >
                  {loading ? 'Generating...' : 'Generate Shareable Link'}
                </button>
              )}
              
              <p style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: 16, lineHeight: 1.5 }}>
                Note: Anyone with this link can view and copy your category protocol. 
                The link can be revoked at any time from your category settings.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareCategoryModal;
